import { Link, useParams } from "react-router-dom";
import { Clock, Check, Trash2, Activity, ChevronRight } from "react-feather";
import {
  selectRecipeById,
  selectCookingHistoryByRecipeId,
  selectActiveProgressByRecipeId,
  removeCookingHistory,
  clearCookingHistoryForRecipe,
  timeAgo,
  formatMultiplier,
  intlLocale,
  selectLocale,
  useT,
  useAppDispatch,
  useAppSelector,
  type RootState,
  type CookingHistory,
} from "@reciperoll/core";
import { NotFoundView } from "../components/NotFoundView";

// One completed-cook row. Web has no swipe, so Delete is a hover-revealed button.
function HistoryRow({ item }: { item: CookingHistory }) {
  const dispatch = useAppDispatch();
  const t = useT();
  const locale = useAppSelector(selectLocale);
  const intl = intlLocale(locale);
  const d = new Date(item.completedAt);
  const dateStr = d.toLocaleDateString(intl, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const timeStr = d.toLocaleTimeString(intl, { hour: "numeric", minute: "2-digit" });
  const mult = item.servingMultiplier;

  return (
    <div className="group flex items-center gap-3.5 rounded-2xl bg-surface-2 p-4">
      <div className="w-10 h-10 rounded-full bg-terracotta/15 flex items-center justify-center shrink-0">
        <Check className="w-[18px] h-[18px] text-terracotta" strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-base font-bold text-ink">{dateStr}</p>
        <p className="text-sm text-ink-soft">
          {timeStr}
          {mult && mult !== 1 ? ` · ${t("recipe.batchLabel", { mult })}` : ""}
        </p>
      </div>
      <span className="text-xs text-ink-faint mr-1 shrink-0">{timeAgo(item.completedAt, locale)}</span>
      <button
        onClick={() => dispatch(removeCookingHistory(item.id))}
        aria-label={t("common.delete")}
        className="w-9 h-9 rounded-lg flex items-center justify-center text-ink-faint hover:text-red-600 hover:bg-surface transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 cursor-pointer shrink-0"
      >
        <Trash2 className="w-[18px] h-[18px]" />
      </button>
    </div>
  );
}

// Per-recipe cooking history: every completed cook, plus a pinned row for an
// in-progress cook. Mirrors the RN `recipe/[id]/history` screen.
export default function RecipeHistory() {
  const t = useT();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const locale = useAppSelector(selectLocale);
  const recipe = useAppSelector((s: RootState) => selectRecipeById(s, id as string));
  const history = useAppSelector((s: RootState) =>
    selectCookingHistoryByRecipeId(s, id as string),
  );
  const active = useAppSelector((s: RootState) =>
    selectActiveProgressByRecipeId(s, id as string),
  );

  if (!recipe) {
    return (
      <NotFoundView
        title={t("recipe.notFoundTitle")}
        message={t("recipe.notFoundMessage")}
      />
    );
  }

  // Wipe every logged cook for this recipe. The in-progress cook (if any) isn't
  // history yet, so it's untouched. Confirmed first: it can't be undone.
  const handleClear = () => {
    if (confirm(t("recipe.clearHistoryMessage", { count: history.length }))) {
      dispatch(clearCookingHistoryForRecipe(id as string));
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-extrabold text-ink">
            {t("recipe.cookingHistory")}
          </h1>
          {recipe.title && (
            <p className="text-sm text-ink-soft mt-1 truncate">{recipe.title}</p>
          )}
        </div>
        {history.length > 0 && (
          <button
            onClick={handleClear}
            className="text-sm font-semibold text-terracotta-ink hover:text-terracotta-dark cursor-pointer shrink-0"
          >
            {t("recipe.clearHistory")}
          </button>
        )}
      </div>

      {history.length === 0 && !active ? (
        <div className="flex flex-col items-center text-center py-16">
          <div className="w-14 h-14 rounded-full bg-surface-2 flex items-center justify-center mb-4">
            <Clock className="w-6 h-6 text-ink-faint" />
          </div>
          <p className="text-lg font-semibold text-ink">{t("recipe.noCooksYet")}</p>
          <p className="text-sm text-ink-soft mt-1 max-w-xs">
            {t("recipe.noCooksDescription")}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {active && (
            <Link
              to={`/recipe/${id}/cook`}
              className="flex items-center gap-3.5 rounded-2xl bg-terracotta/10 p-4 hover:bg-terracotta/15 transition-colors mb-2"
            >
              <div className="w-10 h-10 rounded-full bg-terracotta flex items-center justify-center shrink-0">
                <Activity className="w-[18px] h-[18px] text-white" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-terracotta-ink">
                  {t("recipe.inProgress")}
                </p>
                <p className="text-sm text-ink-soft">
                  {t("recipe.startedAgo", { time: timeAgo(active.startedAt, locale) })}
                  {active.servingMultiplier !== 1
                    ? ` · ${t("recipe.batchLabel", { mult: formatMultiplier(active.servingMultiplier) })}`
                    : ""}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-ink-faint shrink-0" />
            </Link>
          )}

          {history.length > 0 ? (
            <p className="text-sm font-medium text-ink-faint mb-1">
              {t("recipe.cookedTimes", { count: history.length })}
            </p>
          ) : (
            <p className="text-sm text-ink-faint mb-1">{t("recipe.noCompletedCooks")}</p>
          )}

          {history.map((item) => (
            <HistoryRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
